/*
 * AI 问照片「内测申请」下单 + 轮询 hook。
 *
 * 复制自 todayClaw/hooks/useClawPaymentOrder.ts,逻辑一致(下单 → 出二维码 →
 * 轮询订单状态 → 支付成功回调),仅把下单来源标记 sourceDetail 改成照片王统一值
 * AI_PHOTO_KING_SOURCE_DETAIL,与 Claw 链路隔离,避免改动已上线的公共 hook。
 *
 * 设计:hook 不负责关闭弹窗 / 不负责上报,支付成功后只回调 onPaymentSuccess(orderNo),
 * 由调用方(AiPhotoBetaModal)决定后续(调 xiaohei-claw 存库 + 切成功态)。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  createPaymentOrder,
  fetchPaymentOrderStatus
} from '../../../actions/payment'
import paymentConfig from '../../../constants/payment'
import { AI_PHOTO_KING_SOURCE_DETAIL } from '../constants'
import { message } from 'antd'
import { capturePromiseException } from '../../../utils/sentryUtils'

const { channelType, alipayUrlPrefix, paymentStatus } = paymentConfig

/** 轮询配置 */
const POLLING_DELAY = 3000
const POLLING_INVALID_COUNT = Math.floor((5 * 60 * 1000) / POLLING_DELAY) // 5 分钟后二维码失效
const POLLING_MAX_COUNT = Math.floor((30 * 60 * 1000) / POLLING_DELAY) // 30 分钟后停止轮询

interface UseAiPhotoBetaOrderOptions {
  /** 支付成功回调(hook 不会自动关闭弹窗,由调用方控制) */
  onPaymentSuccess?: (orderNo: string) => Promise<void> | void
}

/**
 * @param selectedGoods 形如 { id: goodsID } 的对象;为 null 时不下单(清空二维码)
 */
export function useAiPhotoBetaOrder(
  selectedGoods: { id: string } | null,
  options: UseAiPhotoBetaOrderOptions = {}
) {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state: any) => state.group.groupId)

  const [alipayUrl, setAlipayUrl] = useState('')
  const [wechatUrl, setWechatUrl] = useState('')
  const [channel, setChannel] = useState(channelType.WeChat)
  const [isCreatingOrder, setIsCreatingOrder] = useState(false)
  const [qrCodeInvalid, setQrCodeInvalid] = useState(false)

  const orderNo = useRef('')
  const orderStatusTimer = useRef<any>(null)
  const pollingCount = useRef(0)
  const componentUnmounted = useRef(false)
  const isCreatingOrderRef = useRef(false)
  const lastCreateRef = useRef<{ goodsId: string; time: number }>({
    goodsId: '',
    time: 0
  })

  const channelRef = useRef(channel)
  useEffect(() => {
    channelRef.current = channel
  }, [channel])

  const onPaymentSuccessRef = useRef(options.onPaymentSuccess)
  useEffect(() => {
    onPaymentSuccessRef.current = options.onPaymentSuccess
  }, [options.onPaymentSuccess])

  useEffect(() => {
    return () => {
      componentUnmounted.current = true
      clearInterval(orderStatusTimer.current)
    }
  }, [])

  const clearTimers = useCallback(() => {
    clearInterval(orderStatusTimer.current)
    orderStatusTimer.current = null
  }, [])

  const setCreatingState = useCallback((flag: boolean) => {
    isCreatingOrderRef.current = flag
    setIsCreatingOrder(flag)
  }, [])

  /** 支付成功:只调用回调,不自动关闭弹窗 */
  const handlePaymentSuccess = useCallback(async () => {
    clearTimers()
    console.log('[AiPhotoBeta] 支付成功,orderNo:', orderNo.current)

    if (onPaymentSuccessRef.current) {
      try {
        await onPaymentSuccessRef.current(orderNo.current)
      } catch (e) {
        console.error('[AiPhotoBeta] onPaymentSuccess 回调异常:', e)
      }
    } else {
      message.success('支付成功!')
    }
  }, [clearTimers])

  const handleFetchOrderStatus = useCallback(() => {
    if (pollingCount.current >= POLLING_INVALID_COUNT) {
      setWechatUrl('')
      setAlipayUrl('')
      setQrCodeInvalid(true)
      if (pollingCount.current >= POLLING_MAX_COUNT) {
        clearInterval(orderStatusTimer.current)
        orderStatusTimer.current = null
      }
    }
    dispatch(
      fetchPaymentOrderStatus({
        orderNo: orderNo.current
      })
    )
      .then((data: any) => {
        if (data.status === 0) {
          if (data.payStatus === paymentStatus.Paid.code) {
            handlePaymentSuccess()
          } else if (data.payStatus === paymentStatus.PaymentFail.code) {
            clearTimers()
          }
        }
      })
      .catch(capturePromiseException)
  }, [dispatch, handlePaymentSuccess, clearTimers])

  const pollingOrderStatus = useCallback(() => {
    if (orderStatusTimer.current) {
      clearInterval(orderStatusTimer.current)
      orderStatusTimer.current = null
    }
    orderStatusTimer.current = setInterval(() => {
      if (componentUnmounted.current) {
        clearInterval(orderStatusTimer.current)
        return
      }
      pollingCount.current++
      handleFetchOrderStatus()
    }, POLLING_DELAY)
  }, [handleFetchOrderStatus])

  const createOrder = useCallback(
    (isClickRefresh = false) => {
      const currentChannel = channelRef.current
      clearTimers()
      pollingCount.current = 0

      if (!selectedGoods) return

      const now = Date.now()
      if (isCreatingOrderRef.current) return
      if (
        lastCreateRef.current.goodsId === selectedGoods.id &&
        now - lastCreateRef.current.time < 1000
      ) {
        return
      }
      setCreatingState(true)
      lastCreateRef.current = { goodsId: selectedGoods.id, time: now }

      const params: any = {
        goodsID: selectedGoods.id,
        channel: currentChannel,
        groupID,
        // 2026-06-11 归一:照片王所有下单统一 the_king_of_AI_photo(内测转化
        // 细分由 xiaohei-claw beta_order.source 字段兜底,不丢)
        sourceDetail: AI_PHOTO_KING_SOURCE_DETAIL
      }

      return dispatch(createPaymentOrder(params))
        .then((data: any) => {
          if (data.status === 0) {
            const isWechat = currentChannel === channelType.WeChat
            if (isWechat) {
              setWechatUrl(data.wechat.qrcodeUrl)
              setAlipayUrl('')
            } else {
              setAlipayUrl(alipayUrlPrefix + data.alipay.requestBody)
              setWechatUrl('')
            }
            setQrCodeInvalid(false)
            orderNo.current = data.orderNo
            pollingOrderStatus()
          } else if (data.status === 9) {
            message.error('其他成员正在创建订单')
            setCreatingState(true)
          } else {
            message.error(data.msg)
            setAlipayUrl('')
            setWechatUrl('')
          }
          return data
        })
        .catch((err) => {
          capturePromiseException(err)
          throw err
        })
        .finally(() => {
          setCreatingState(false)
        })
    },
    [selectedGoods, clearTimers, dispatch, groupID, pollingOrderStatus]
  )

  // selectedGoods 变化时自动创建订单
  useEffect(() => {
    if (selectedGoods) {
      createOrder()
    } else {
      setAlipayUrl('')
      setWechatUrl('')
      setQrCodeInvalid(false)
    }
  }, [selectedGoods])

  // channel 变化时重新创建订单
  useEffect(() => {
    if (selectedGoods) {
      createOrder(true)
    }
  }, [channel])

  return {
    alipayUrl,
    wechatUrl,
    qrCodeInvalid,
    channel,
    setChannel: (c: number) => {
      setChannel(c)
      channelRef.current = c
    },
    isCreatingOrder,
    createOrder,
    clearTimers
  }
}
